import React, { useEffect, useState, useCallback } from "react";
import API from "../../../services/api";

// ── Reusable Simple Table ─────────────────────────────────────
const SimpleTable = ({ columns, data, onEdit, onDelete, deleting }) => (
  <div className="table-responsive">
    <table className="table table-hover align-middle mb-0">
      <thead className="bg-light">
        <tr>
          <th className="px-3 py-3 small text-muted fw-semibold">#</th>
          {columns.map((c) => (
            <th key={c.key} className="py-3 small text-muted fw-semibold">
              {c.label}
            </th>
          ))}
          <th className="py-3 small text-muted fw-semibold text-end pe-3">
            Actions
          </th>
        </tr>
      </thead>
      <tbody>
        {data.length === 0 ? (
          <tr>
            <td
              colSpan={columns.length + 2}
              className="text-center py-4 text-muted small"
            >
              No records found. Add one above.
            </td>
          </tr>
        ) : (
          data.map((row, idx) => (
            <tr key={row.id}>
              <td className="px-3 text-muted small">{idx + 1}</td>
              {columns.map((c) => (
                <td key={c.key} className="small">
                  {c.badge ? (
                    <span
                      className={`badge ${row[c.key] ? "bg-success-subtle text-success" : "bg-secondary-subtle text-secondary"}`}
                    >
                      {row[c.key] ? "Active" : "Inactive"}
                    </span>
                  ) : (
                    row[c.key] || "—"
                  )}
                </td>
              ))}
              <td className="text-end pe-3">
                <button
                  className="btn btn-sm btn-outline-primary me-2"
                  onClick={() => onEdit(row)}
                >
                  <i className="bi bi-pencil"></i>
                </button>
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => onDelete(row.id)}
                  disabled={deleting === row.id}
                >
                  {deleting === row.id ? (
                    <span className="spinner-border spinner-border-sm"></span>
                  ) : (
                    <i className="bi bi-trash"></i>
                  )}
                </button>
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

// ── Simple Add/Edit Form ──────────────────────────────────────
const InlineForm = ({
  fields,
  form,
  setForm,
  onSave,
  onCancel,
  saving,
  editItem,
}) => (
  <div className="card border-0 bg-light rounded-3 mb-3">
    <div className="card-body p-3">
      <div className="row g-2 align-items-end">
        {fields.map((f) => (
          <div key={f.key} className={f.col || "col-12 col-md-4"}>
            <label className="form-label small fw-medium mb-1">{f.label}</label>
            {f.type === "select" ? (
              <select
                className="form-select form-select-sm"
                value={form[f.key] || ""}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
              >
                <option value="">Select {f.label}</option>
                {f.options?.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type={f.type || "text"}
                className="form-control form-control-sm"
                placeholder={f.placeholder || f.label}
                value={form[f.key] || ""}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
              />
            )}
          </div>
        ))}
        <div className="col-12 col-md-3 d-flex gap-2">
          <button
            className="btn btn-sm text-white fw-medium px-3"
            style={{ background: "linear-gradient(135deg, #1a3c5e, #2d6a4f)" }}
            onClick={onSave}
            disabled={saving}
          >
            {saving ? (
              <span className="spinner-border spinner-border-sm"></span>
            ) : (
              <>
                <i
                  className={`bi ${editItem ? "bi-check-lg" : "bi-plus-lg"} me-1`}
                ></i>
                {editItem ? "Update" : "Add"}
              </>
            )}
          </button>
          {editItem && (
            <button className="btn btn-sm btn-light px-3" onClick={onCancel}>
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  </div>
);

// ── Masters Page ──────────────────────────────────────────────
const Masters = () => {
  const [activeTab, setActiveTab] = useState("zp");
  const [activeGeo, setActiveGeo] = useState("zp");
  const [activeOther, setActiveOther] = useState("dept");

  // Data states
  const [zpList, setZpList] = useState([]);
  const [tpList, setTpList] = useState([]);
  const [gpList, setGpList] = useState([]);
  const [villList, setVillList] = useState([]);
  const [boothList, setBoothList] = useState([]);
  const [deptList, setDeptList] = useState([]);
  const [prjCatList, setPrjCatList] = useState([]);
  const [grvCatList, setGrvCatList] = useState([]);
  const [hoaList, setHoaList] = useState([]);
  const [casteList, setCasteList] = useState([]);

  // Form states
  const [form, setForm] = useState({});
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [loading, setLoading] = useState(false);

  // ── Fetch All ─────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [zp, tp, gp, vill, booth, dept, prjcat, grvcat, hoa, caste] =
        await Promise.all([
          API.get("/masters/zp"),
          API.get("/masters/tp"),
          API.get("/masters/gp"),
          API.get("/masters/villages"),
          API.get("/masters/booths"),
          API.get("/masters/departments"),
          API.get("/masters/projectcategories"),
          API.get("/grievances/categories"),
          API.get("/masters/headofaccounts"),
          API.get("/masters/castes"),
        ]);
      setZpList(zp.data.data);
      setTpList(tp.data.data);
      setGpList(gp.data.data);
      setVillList(vill.data.data);
      setBoothList(booth.data.data);
      setDeptList(dept.data.data);
      setPrjCatList(prjcat.data.data);
      setGrvCatList(grvcat.data.data);
      setHoaList(hoa.data.data);
      setCasteList(caste.data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // ── Reset Form ────────────────────────────────────────────
  const resetForm = () => {
    setForm({});
    setEditItem(null);
  };

  // ── Edit ──────────────────────────────────────────────────
  const handleEdit = (item) => {
    setEditItem(item);
    setForm({ ...item });
  };

  // ── Delete ────────────────────────────────────────────────
  const handleDelete = async (endpoint, id, refresh) => {
    if (!window.confirm("Delete this record?")) return;
    setDeleting(id);
    try {
      await API.delete(`/masters/${endpoint}/${id}`);
      refresh();
    } catch (err) {
      alert(err.response?.data?.message || "Delete failed");
    } finally {
      setDeleting(null);
    }
  };

  // ── Save ──────────────────────────────────────────────────
  const handleSave = async (endpoint, payload, refresh) => {
    setSaving(true);
    try {
      if (editItem) {
        await API.put(`/masters/${endpoint}/${editItem.id}`, payload);
      } else {
        await API.post(`/masters/${endpoint}`, payload);
      }
      resetForm();
      refresh();
    } catch (err) {
      alert(err.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // ── Tab Config ────────────────────────────────────────────
  const geoTabs = [
    { key: "zp", label: "ZP", icon: "bi-geo-alt" },
    { key: "tp", label: "TP", icon: "bi-geo" },
    { key: "gp", label: "GP", icon: "bi-pin-map" },
    { key: "village", label: "Village", icon: "bi-houses" },
    { key: "booth", label: "Booth", icon: "bi-building" },
  ];

  const otherTabs = [
    { key: "dept", label: "Department", icon: "bi-briefcase" },
    { key: "prjcat", label: "Project Category", icon: "bi-folder" },
    { key: "grvcat", label: "Grievance Cat.", icon: "bi-chat" },
    { key: "hoa", label: "Head of Account", icon: "bi-bank" },
    { key: "caste", label: "Caste", icon: "bi-people" },
  ];

  // ── ZP Options for selects ────────────────────────────────
  const zpOptions = zpList.map((z) => ({ value: z.zpid, label: z.zpname }));
  const tpOptions = tpList.map((t) => ({ value: t.tpid, label: t.tpname }));
  const gpOptions = gpList.map((g) => ({ value: g.gpid, label: g.gpname }));
  const villOptions = villList.map((v) => ({
    value: v.villageid,
    label: v.name,
  }));

  return (
    <div>
      {/* Header */}
      <div className="mb-4">
        <h4 className="fw-bold mb-1">Masters</h4>
        <p className="text-muted small mb-0">
          Configure system data — Geography, Departments, Categories
        </p>
      </div>

      {/* Main Tabs — Geography / Others */}
      <ul className="nav nav-pills mb-4 gap-2">
        <li className="nav-item">
          <button
            className={`nav-link px-4 fw-medium ${activeTab === "geo" || activeTab === "zp" || activeTab === "tp" || activeTab === "gp" || activeTab === "village" || activeTab === "booth" ? "active" : ""}`}
            style={
              activeTab === "geo" ||
              ["zp", "tp", "gp", "village", "booth"].includes(activeTab)
                ? {
                    background: "linear-gradient(135deg, #1a3c5e, #2d6a4f)",
                    color: "white",
                  }
                : {}
            }
            onClick={() => {
              setActiveTab("zp");
              setActiveGeo("zp");
            }}
          >
            <i className="bi bi-globe me-2"></i>Geography
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link px-4 fw-medium ${["dept", "prjcat", "grvcat", "hoa", "caste"].includes(activeTab) ? "active" : ""}`}
            style={
              ["dept", "prjcat", "grvcat", "hoa", "caste"].includes(activeTab)
                ? {
                    background: "linear-gradient(135deg, #1a3c5e, #2d6a4f)",
                    color: "white",
                  }
                : {}
            }
            onClick={() => {
              setActiveTab("dept");
              setActiveOther("dept");
            }}
          >
            <i className="bi bi-grid me-2"></i>Others
          </button>
        </li>
      </ul>

      {/* Geography Sub Tabs */}
      {["zp", "tp", "gp", "village", "booth"].includes(activeTab) && (
        <div className="card border-0 shadow-sm rounded-4 mb-3">
          <div className="card-header bg-white border-0 pt-3 pb-0">
            <ul className="nav nav-tabs border-0 gap-1">
              {geoTabs.map((t) => (
                <li key={t.key} className="nav-item">
                  <button
                    className={`nav-link small fw-medium px-3 ${activeGeo === t.key ? "active border-bottom border-2" : ""}`}
                    style={{
                      borderColor:
                        activeGeo === t.key ? "#1a3c5e" : "transparent",
                      color: activeGeo === t.key ? "#1a3c5e" : "",
                    }}
                    onClick={() => {
                      setActiveGeo(t.key);
                      setActiveTab(t.key);
                      resetForm();
                    }}
                  >
                    <i className={`bi ${t.icon} me-1`}></i>
                    {t.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div className="card-body p-3">
            {/* ── ZP ── */}
            {activeTab === "zp" && (
              <>
                <InlineForm
                  fields={[
                    {
                      key: "zpname",
                      label: "ZP Name",
                      placeholder: "Enter Zilla Panchayat name",
                    },
                  ]}
                  form={form}
                  setForm={setForm}
                  editItem={editItem}
                  saving={saving}
                  onCancel={resetForm}
                  onSave={() =>
                    handleSave("zp", { zpname: form.zpname }, fetchAll)
                  }
                />
                <SimpleTable
                  columns={[{ key: "zpname", label: "ZP Name" }]}
                  data={zpList.map((z) => ({ ...z, id: z.zpid }))}
                  onEdit={handleEdit}
                  onDelete={(id) => handleDelete("zp", id, fetchAll)}
                  deleting={deleting}
                />
              </>
            )}

            {/* ── TP ── */}
            {activeTab === "tp" && (
              <>
                <InlineForm
                  fields={[
                    {
                      key: "zpid",
                      label: "Zilla Panchayat",
                      type: "select",
                      options: zpOptions,
                      col: "col-12 col-md-4",
                    },
                    {
                      key: "tpname",
                      label: "TP Name",
                      placeholder: "Enter Taluk Panchayat name",
                      col: "col-12 col-md-4",
                    },
                  ]}
                  form={form}
                  setForm={setForm}
                  editItem={editItem}
                  saving={saving}
                  onCancel={resetForm}
                  onSave={() =>
                    handleSave(
                      "tp",
                      { tpname: form.tpname, zpid: parseInt(form.zpid) },
                      fetchAll,
                    )
                  }
                />
                <SimpleTable
                  columns={[
                    { key: "tpname", label: "TP Name" },
                    { key: "zpname", label: "Under ZP" },
                  ]}
                  data={tpList.map((t) => ({
                    ...t,
                    id: t.tpid,
                    zpname:
                      zpList.find((z) => z.zpid === t.zpid)?.zpname || "—",
                  }))}
                  onEdit={handleEdit}
                  onDelete={(id) => handleDelete("tp", id, fetchAll)}
                  deleting={deleting}
                />
              </>
            )}

            {/* ── GP ── */}
            {activeTab === "gp" && (
              <>
                <InlineForm
                  fields={[
                    {
                      key: "zpid",
                      label: "Zilla Panchayat",
                      type: "select",
                      options: zpOptions,
                      col: "col-12 col-md-3",
                    },
                    {
                      key: "tpid",
                      label: "Taluk Panchayat",
                      type: "select",
                      options: tpOptions.filter(
                        (t) =>
                          !form.zpid ||
                          tpList.find((tp) => tp.tpid === parseInt(t.value))
                            ?.zpid === parseInt(form.zpid),
                      ),
                      col: "col-12 col-md-3",
                    },
                    {
                      key: "gpname",
                      label: "GP Name",
                      placeholder: "Enter Gram Panchayat name",
                      col: "col-12 col-md-3",
                    },
                  ]}
                  form={form}
                  setForm={setForm}
                  editItem={editItem}
                  saving={saving}
                  onCancel={resetForm}
                  onSave={() =>
                    handleSave(
                      "gp",
                      {
                        gpname: form.gpname,
                        zpid: parseInt(form.zpid),
                        tpid: parseInt(form.tpid),
                      },
                      fetchAll,
                    )
                  }
                />
                <SimpleTable
                  columns={[
                    { key: "gpname", label: "GP Name" },
                    { key: "tpname", label: "Under TP" },
                    { key: "zpname", label: "Under ZP" },
                  ]}
                  data={gpList.map((g) => ({
                    ...g,
                    id: g.gpid,
                    tpname:
                      tpList.find((t) => t.tpid === g.tpid)?.tpname || "—",
                    zpname:
                      zpList.find((z) => z.zpid === g.zpid)?.zpname || "—",
                  }))}
                  onEdit={handleEdit}
                  onDelete={(id) => handleDelete("gp", id, fetchAll)}
                  deleting={deleting}
                />
              </>
            )}

            {/* ── Village ── */}
            {activeTab === "village" && (
              <>
                <InlineForm
                  fields={[
                    {
                      key: "zpid",
                      label: "ZP",
                      type: "select",
                      options: zpOptions,
                      col: "col-6 col-md-2",
                    },
                    {
                      key: "tpid",
                      label: "TP",
                      type: "select",
                      options: tpOptions.filter(
                        (t) =>
                          !form.zpid ||
                          tpList.find((tp) => tp.tpid === parseInt(t.value))
                            ?.zpid === parseInt(form.zpid),
                      ),
                      col: "col-6 col-md-2",
                    },
                    {
                      key: "gpid",
                      label: "GP",
                      type: "select",
                      options: gpOptions.filter(
                        (g) =>
                          !form.tpid ||
                          gpList.find((gp) => gp.gpid === parseInt(g.value))
                            ?.tpid === parseInt(form.tpid),
                      ),
                      col: "col-6 col-md-2",
                    },
                    {
                      key: "name",
                      label: "Village Name",
                      placeholder: "Village name",
                      col: "col-6 col-md-3",
                    },
                  ]}
                  form={form}
                  setForm={setForm}
                  editItem={editItem}
                  saving={saving}
                  onCancel={resetForm}
                  onSave={() =>
                    handleSave(
                      "villages",
                      {
                        name: form.name,
                        zpid: parseInt(form.zpid),
                        tpid: parseInt(form.tpid),
                        gpid: parseInt(form.gpid),
                      },
                      fetchAll,
                    )
                  }
                />
                <SimpleTable
                  columns={[
                    { key: "name", label: "Village Name" },
                    { key: "gpname", label: "Under GP" },
                    { key: "tpname", label: "Under TP" },
                    { key: "zpname", label: "Under ZP" },
                  ]}
                  data={villList.map((v) => ({
                    ...v,
                    id: v.villageid,
                    gpname:
                      gpList.find((g) => g.gpid === v.gpid)?.gpname || "—",
                    tpname:
                      tpList.find((t) => t.tpid === v.tpid)?.tpname || "—",
                    zpname:
                      zpList.find((z) => z.zpid === v.zpid)?.zpname || "—",
                  }))}
                  onEdit={handleEdit}
                  onDelete={(id) => handleDelete("villages", id, fetchAll)}
                  deleting={deleting}
                />
              </>
            )}

            {/* ── Booth ── */}
            {activeTab === "booth" && (
              <>
                <InlineForm
                  fields={[
                    {
                      key: "villageid",
                      label: "Village",
                      type: "select",
                      options: villOptions,
                      col: "col-12 col-md-3",
                    },
                    {
                      key: "booth_no",
                      label: "Booth No",
                      type: "number",
                      col: "col-6 col-md-2",
                    },
                    {
                      key: "booth_name",
                      label: "Booth Name",
                      placeholder: "Booth name",
                      col: "col-6 col-md-3",
                    },
                  ]}
                  form={form}
                  setForm={setForm}
                  editItem={editItem}
                  saving={saving}
                  onCancel={resetForm}
                  onSave={() =>
                    handleSave(
                      "booths",
                      {
                        villageid: parseInt(form.villageid),
                        booth_no: parseInt(form.booth_no),
                        booth_name: form.booth_name,
                      },
                      fetchAll,
                    )
                  }
                />
                <SimpleTable
                  columns={[
                    { key: "booth_no", label: "Booth No" },
                    { key: "booth_name", label: "Booth Name" },
                    { key: "villname", label: "Village" },
                  ]}
                  data={boothList.map((b) => ({
                    ...b,
                    id: b.boothid,
                    villname:
                      villList.find((v) => v.villageid === b.villageid)?.name ||
                      "—",
                  }))}
                  onEdit={handleEdit}
                  onDelete={(id) => handleDelete("booths", id, fetchAll)}
                  deleting={deleting}
                />
              </>
            )}
          </div>
        </div>
      )}

      {/* Others Sub Tabs */}
      {["dept", "prjcat", "grvcat", "hoa", "caste"].includes(activeTab) && (
        <div className="card border-0 shadow-sm rounded-4 mb-3">
          <div className="card-header bg-white border-0 pt-3 pb-0">
            <ul className="nav nav-tabs border-0 gap-1 flex-wrap">
              {otherTabs.map((t) => (
                <li key={t.key} className="nav-item">
                  <button
                    className={`nav-link small fw-medium px-3 ${activeOther === t.key ? "active" : ""}`}
                    style={{
                      borderBottom:
                        activeOther === t.key ? "2px solid #1a3c5e" : "none",
                      color: activeOther === t.key ? "#1a3c5e" : "",
                    }}
                    onClick={() => {
                      setActiveOther(t.key);
                      setActiveTab(t.key);
                      resetForm();
                    }}
                  >
                    <i className={`bi ${t.icon} me-1`}></i>
                    {t.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div className="card-body p-3">
            {/* ── Department ── */}
            {activeTab === "dept" && (
              <>
                <InlineForm
                  fields={[
                    {
                      key: "deptname",
                      label: "Department Name",
                      placeholder: "e.g. Public Works, Health",
                    },
                  ]}
                  form={form}
                  setForm={setForm}
                  editItem={editItem}
                  saving={saving}
                  onCancel={resetForm}
                  onSave={() =>
                    handleSave(
                      "departments",
                      { deptname: form.deptname },
                      fetchAll,
                    )
                  }
                />
                <SimpleTable
                  columns={[{ key: "deptname", label: "Department Name" }]}
                  data={deptList.map((d) => ({ ...d, id: d.deptid }))}
                  onEdit={handleEdit}
                  onDelete={(id) => handleDelete("departments", id, fetchAll)}
                  deleting={deleting}
                />
              </>
            )}

            {/* ── Project Category ── */}
            {activeTab === "prjcat" && (
              <>
                <InlineForm
                  fields={[
                    {
                      key: "prjcatname",
                      label: "Project Category",
                      placeholder: "e.g. Roads, Buildings",
                    },
                  ]}
                  form={form}
                  setForm={setForm}
                  editItem={editItem}
                  saving={saving}
                  onCancel={resetForm}
                  onSave={() =>
                    handleSave(
                      "projectcategories",
                      { prjcatname: form.prjcatname },
                      fetchAll,
                    )
                  }
                />
                <SimpleTable
                  columns={[{ key: "prjcatname", label: "Category Name" }]}
                  data={prjCatList.map((p) => ({ ...p, id: p.prjcatid }))}
                  onEdit={handleEdit}
                  onDelete={(id) =>
                    handleDelete("projectcategories", id, fetchAll)
                  }
                  deleting={deleting}
                />
              </>
            )}

            {/* ── Grievance Category ── */}
            {activeTab === "grvcat" && (
              <>
                <InlineForm
                  fields={[
                    {
                      key: "grvcategory",
                      label: "Grievance Category",
                      placeholder: "e.g. Water, Roads, Electricity",
                    },
                  ]}
                  form={form}
                  setForm={setForm}
                  editItem={editItem}
                  saving={saving}
                  onCancel={resetForm}
                  onSave={() =>
                    handleSave(
                      "grievance-categories",
                      { grvcategory: form.grvcategory },
                      fetchAll,
                    )
                  }
                />
                <SimpleTable
                  columns={[{ key: "grvcategory", label: "Category Name" }]}
                  data={grvCatList.map((g) => ({ ...g, id: g.grvcatid }))}
                  onEdit={handleEdit}
                  onDelete={(id) =>
                    handleDelete("grievance-categories", id, fetchAll)
                  }
                  deleting={deleting}
                />
              </>
            )}

            {/* ── Head of Account ── */}
            {activeTab === "hoa" && (
              <>
                <InlineForm
                  fields={[
                    {
                      key: "headaccount",
                      label: "Head of Account",
                      placeholder: "e.g. State Fund, Central Fund",
                    },
                  ]}
                  form={form}
                  setForm={setForm}
                  editItem={editItem}
                  saving={saving}
                  onCancel={resetForm}
                  onSave={() =>
                    handleSave(
                      "headofaccounts",
                      { headaccount: form.headaccount },
                      fetchAll,
                    )
                  }
                />
                <SimpleTable
                  columns={[{ key: "headaccount", label: "Account Name" }]}
                  data={hoaList.map((h) => ({ ...h, id: h.headaccountid }))}
                  onEdit={handleEdit}
                  onDelete={(id) =>
                    handleDelete("headofaccounts", id, fetchAll)
                  }
                  deleting={deleting}
                />
              </>
            )}

            {/* ── Caste ── */}
            {activeTab === "caste" && (
              <>
                <InlineForm
                  fields={[
                    {
                      key: "castename",
                      label: "Caste Name",
                      placeholder: "e.g. General, OBC, SC, ST",
                    },
                  ]}
                  form={form}
                  setForm={setForm}
                  editItem={editItem}
                  saving={saving}
                  onCancel={resetForm}
                  onSave={() =>
                    handleSave(
                      "castes",
                      { castename: form.castename },
                      fetchAll,
                    )
                  }
                />
                <SimpleTable
                  columns={[{ key: "castename", label: "Caste Name" }]}
                  data={casteList.map((c) => ({ ...c, id: c.casteid }))}
                  onEdit={handleEdit}
                  onDelete={(id) => handleDelete("castes", id, fetchAll)}
                  deleting={deleting}
                />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Masters;
